<template>
  <div v-if="failed" class="load-state load-state-failed" role="alert">
    <p class="load-state-msg">{{ message }}</p>
    <button type="button" class="load-state-retry" @click="emit('retry')">Try again</button>
  </div>
  <div v-else-if="loading && shown" class="load-state load-state-loading">
    <BreathDots :sr-label="loadingLabel" :still-after="stillAfter" />
  </div>
</template>

<script setup>
// One quiet loading / failed state for any list that fetches data.
//   loading: the app's one loading look, BreathDots (Chris 2026-10-19;
//            recipient polish §4, pattern B), shown only after `delay` ms so
//            fast loads never flash; "Still with you…" once it runs long
//   failed : one kind line + "Try again" (emits `retry`)
// The empty state stays with the list (its copy is specific to each list).
import { ref, watch, onBeforeUnmount } from 'vue'
import BreathDots from '@/components/ui/BreathDots.vue'

const props = defineProps({
  loading: { type: Boolean, default: false },
  failed: { type: Boolean, default: false },
  message: { type: String, default: "Couldn't load this just now." },
  loadingLabel: { type: String, default: 'Loading' },
  delay: { type: Number, default: 250 },
  // "Still with you…" after this long on screen (0 = never).
  stillAfter: { type: Number, default: 8000 },
})
const emit = defineEmits(['retry'])

const shown = ref(false)
let timer = null
function clear() { if (timer) { clearTimeout(timer); timer = null } }

watch(() => props.loading, (on) => {
  clear()
  if (!on) { shown.value = false; return }
  if (props.delay <= 0) { shown.value = true; return }
  timer = setTimeout(() => { shown.value = true; timer = null }, props.delay)
}, { immediate: true })

onBeforeUnmount(clear)
</script>

<style scoped>
.load-state { display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 6px; padding: 28px 16px; text-align: center; }
.load-state-msg { margin: 0; font-size: 14px; color: rgba(195, 169, 255, 0.75); }
.load-state-retry { min-height: 44px; min-width: 44px; padding: 0 16px; background: none; border: none;
  color: #FFDFA3; font-size: 14px; text-decoration: underline; text-underline-offset: 3px; cursor: pointer; }
.load-state-retry:hover { color: #f0d9a3; }
</style>
